import { type AttributeValue, attributes, type HtmlValue, html, joinClasses } from 'BomdisoftComponents/html/index';
import { OPEN_IN_NEW_ROUNDED_GLYPH } from 'BomdisoftComponents/icons/glyphs';
import { renderIcon } from 'BomdisoftComponents/icons/render';

export type ButtonColor = 'error' | 'info' | 'neutral' | 'success' | 'warning';
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonVariant = 'contained' | 'outlined' | 'text' | 'tint';
export type IconButtonSize = 'small' | 'medium' | 'large';

type ButtonLook = {
  color?: ButtonColor;
  fullWidth?: boolean;
  size?: ButtonSize;
  variant?: ButtonVariant;
};

export type ButtonOptions = ButtonLook & {
  attributes?: Readonly<Record<string, AttributeValue>>;
  children?: HtmlValue;
  className?: string;
  endIcon?: HtmlValue;
  startIcon?: HtmlValue;
  type?: 'button' | 'reset' | 'submit';
};

export type LinkButtonOptions = Omit<ButtonOptions, 'type'> & {
  external?: boolean;
  href: string;
};

export type IconButtonOptions = {
  attributes?: Readonly<Record<string, AttributeValue>>;
  children?: HtmlValue;
  className?: string;
  label: string;
  size?: IconButtonSize;
  type?: 'button' | 'reset' | 'submit';
};

export const toButtonColor = (color: ButtonColor | undefined, variant: ButtonVariant): ButtonColor => color ?? (variant === 'outlined' ? 'neutral' : 'info');

const toButtonAttributes = ({ color, fullWidth, size = 'medium', variant = 'text' }: ButtonLook) => ({
  'data-color': toButtonColor(color, variant),
  'data-full-width': fullWidth,
  'data-size': size,
  'data-variant': variant,
});

const renderStartIcon = (startIcon: HtmlValue) => (startIcon ? html`<span class="bomdisoft-button-start-icon">${startIcon}</span>` : '');

const renderEndIcon = (endIcon: HtmlValue) => (endIcon ? html`<span class="bomdisoft-button-end-icon">${endIcon}</span>` : '');

export const renderButton = ({ attributes: extra = {}, children, className, endIcon, startIcon, type = 'button', ...look }: ButtonOptions) =>
  html`<button${attributes({ class: joinClasses('bomdisoft-button', className), ...toButtonAttributes(look), type, ...extra })}>${renderStartIcon(startIcon)}${children}${renderEndIcon(endIcon)}</button>`;

export const renderLinkButton = ({ attributes: extra = {}, children, className, endIcon, external, href, startIcon, ...look }: LinkButtonOptions) =>
  html`<a${attributes({ class: joinClasses('bomdisoft-button', className), ...toButtonAttributes(look), href, rel: external ? 'noopener noreferrer' : undefined, target: external ? '_blank' : undefined, ...extra })}>${renderStartIcon(startIcon)}${children}${external ? renderIcon(OPEN_IN_NEW_ROUNDED_GLYPH, { size: 16 }) : renderEndIcon(endIcon)}</a>`;

export const renderIconButton = ({ attributes: extra = {}, children, className, label, size = 'medium', type = 'button' }: IconButtonOptions) =>
  html`<button${attributes({ 'aria-label': label, class: joinClasses('bomdisoft-icon-button', className), 'data-size': size, type, ...extra })}>${children}</button>`;
